'use client';

import { Bell } from 'lucide-react';
import { useT } from '@/components/i18n-provider';
import { Tip } from '@/components/ui/tooltip';
import { useAuth } from '@/components/auth-provider';
import { useNotifications } from '@/hooks/useNotifications';
import {
  toggleWorkspacePanel,
  useWorkspacePanelOpen,
} from './panels/panel-events';
import { cn } from '@/lib/utils';

/**
 * Top-right floating Notifications button. Opens the workspace's top
 * sheet panel (`id="top"`) which embeds the full `/notifications` page.
 * Shows a small unread badge if the signed-in user has notifications.
 */
export function NotificationsFab({ className }: { className?: string }) {
  const t = useT();
  const { user } = useAuth();
  const { unreadCount } = useNotifications(!!user);
  // Mirror the notifications panel's open state so the FAB reads as a
  // toggle: while the panel is visible, the button paints a "pressed"
  // sunken state (inset shadow + darker surface + slight scale) so the
  // user gets a clear "this is the one that's open" cue without losing
  // the bell glyph or the unread badge.
  const panelOpen = useWorkspacePanelOpen('top');

  return (
    <Tip label={panelOpen ? t('fab.closeNotifications') : t('nav.notifications')}>
      <button
        type="button"
        onClick={() => toggleWorkspacePanel('top')}
        aria-label={panelOpen ? t('fab.closeNotifications') : t('fab.openNotifications')}
        aria-expanded={panelOpen}
        aria-pressed={panelOpen}
        // Onboarding overlay anchor — `notifications` caption pins
        // to this bell. Replaces the standalone `skip` scribble in
        // the top-right corner.
        data-onboarding-anchor="notifications"
        className={cn(
          'pointer-events-auto relative inline-flex h-10 w-10 items-center justify-center rounded-full select-none transition-all',
          // `liquid-glass` ALWAYS applies — `.liquid-glass.ws-fab-pressed`
          // (globals.css) overrides only the surface tint and inset
          // shadows AND swaps the displacement filter URL to
          // `#liquid-glass-pill-active` so the bezel curve reads
          // deeper when the notifications panel is open. The liquid
          // glass refraction never goes away — it transitions to a
          // different pattern.
          'liquid-glass',
          !panelOpen && 'hover:shadow-[0_4px_18px_rgba(0,0,0,0.10)]',
          // Pressed: shared utility (see globals.css → ws-fab-pressed)
          // so this matches the Activity, Create and Brand FABs when
          // their respective panels/menus are open. Same inset
          // darkening, same hairline, same scale.
          panelOpen && 'ws-fab-pressed',
          className,
        )}
      >
        <Bell
          className={cn(
            'h-4 w-4 transition-colors',
            panelOpen ? 'text-foreground' : 'text-foreground/80',
          )}
        />
        {unreadCount > 0 && (
          <span
            aria-hidden
            className="absolute -top-0.5 -right-0.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-semibold leading-none text-white shadow-sm"
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>
    </Tip>
  );
}
